import React, {
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import "./ChatInput.css";
import AttachmentIcon from "@mui/icons-material/Attachment";
import MoodIcon from "@mui/icons-material/Mood";
import { ChatContext } from "../../../contexts/ChatContext";
import Picker from "emoji-picker-react";
import SendIcon from "@mui/icons-material/Send";
import { connect } from "react-redux";
import { setCurrentUser } from "../../../redux/user/user.actions";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  Box,
  IconButton,
  Paper,
  TextField,
} from "@mui/material";
import { socket } from "../Socket/Socket";

const ChatInput = ({
  disable,
  setDisable,
  handleReceiveMessage,
  currentUser,
  messageType,
  setMessageType,
  selectedFile,
  setSelectedFile,
  selectedFileName,
  setSelectedFileName,
}) => {
  const [message, setMessage] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const { selectedContactId, selectedGroupId, audios, setAudios } =
    useContext(ChatContext);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const pickerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const togglePicker = () => setShowPicker(!showPicker);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTextChange = (e) => {
    setMessage(e.target.value);
    setMessageType("text");
    setSelectedFile(null);
    setSelectedFileName(null);
    setAudios([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setSelectedFileName(file.name);
    setMessageType("file");
    setAudios([]);
    setMessage("");
    setDisable(true);
  };

  const sendFileMessage = () => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result;
      sendMessage(base64Data, "file", selectedFileName);
    };
    reader.readAsDataURL(selectedFile);
  };

  const sendTextMessage = () => sendMessage(message, "text", "");

  const sendAudioMessage = () => {
    const reader = new FileReader();
    reader.onloadend = () => {
      sendMessage(reader.result, "audio", "");
    };
    reader.readAsDataURL(audios[0].data);
  };

  const sendChat = (type) => {
    switch (type) {
      case "text":
        sendTextMessage();
        break;
      case "file":
        sendFileMessage();
        break;
      case "audio":
        sendAudioMessage();
        break;
      default:
        break;
    }
    setShowPicker(false);
  };

  const sendMessage = (messages, messageTypes, fileName) => {
    try {
      if (selectedContactId) {
        socket.emit("send_personal_message", {
          senderUserId: currentUser._id,
          receiverUserId: selectedContactId,
          content: messages,
          contentType: messageTypes,
          fileName,
          userName: currentUser.username,
        });
        socket.on("receive_personal_message", handleReceiveMessage);
      } else if (selectedGroupId) {
        socket.emit("send_message_in_group", {
          senderId: currentUser._id,
          groupId: selectedGroupId,
          content: messages,
          contentType: messageTypes,
          fileName,
          userName: currentUser.username,
        });
        socket.on("receive_group_message", handleReceiveMessage);
      }

      setMessage("");
      setSelectedFile(null);
      setSelectedFileName(null);
      setAudios([]);
      setMessageType("");
      setDisable(false);
    } catch (error) {
      console.error("Error calling backend API:", error.message);
    }
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      sendChat(messageType);
    }
  };

  return (
    <Grid2
      container
      ref={containerRef}
      sx={{
        position: "relative",
        //background: "rgba(255, 255, 255, 0.9)",
        // padding: 1,
      }}
    >
      <Grid2 xs={12}>
        {/* Chat Input Container */}
        <Paper
          elevation={2}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            // padding: "8px 12px",
            borderRadius: "12px",
            //backgroundColor: "#f8f9fa",
            //gap: 1.5,
          }}
        >
          {/* Emoji Picker */}
          <IconButton onClick={togglePicker}>
            <MoodIcon sx={{ fontSize: 30 }} />
          </IconButton>

          {/* Emoji Picker Container */}
          {showPicker && (
            <Box
              ref={pickerRef}
              sx={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                mb: 1,
                zIndex: 1500, // Higher z-index to ensure it's above everything
                boxShadow: 3,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Picker
                searchDisabled
                style={{
                  width: windowWidth < 416 ? "250px" : "350px",
                  height: "300px",
                }}
                onEmojiClick={(emoji) => {
                  setMessage((prev) => prev + emoji.emoji);
                  setMessageType("text");
                  setSelectedFile(null);
                  setSelectedFileName(null);
                  setAudios([]);
                }}
              />
            </Box>
          )}

          {/* Text Input */}
          <TextField
            fullWidth
            variant="standard"
            placeholder="Type a message..."
            value={message}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            disabled={disable}
            InputProps={{ disableUnderline: true }}
            sx={{
              fontSize: "17px",
              px: 1,
              '& .MuiInputBase-input': {
                color: '#000000',
              }
            }}
          />

          {/* File Input */}
          <IconButton component="label">
            <AttachmentIcon sx={{ fontSize: 30 }} />
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
              onChange={handleFileChange}
            />
          </IconButton>

          {/* Send Button */}
          <IconButton onClick={() => sendChat(messageType)}>
            <SendIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Paper>
      </Grid2>
    </Grid2>
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.user.currentUser,
});
const mapDispatchToProps = (dispatch) => ({
  setCurrentUser: (user) => dispatch(setCurrentUser(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatInput);